import {Injectable, Injector} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '@env/environment';
import {forkJoin, Observable, of, Subject} from 'rxjs';
import {Page} from '../domain/page';
import {Product, ProductAttributeDefinition, ProductVariant, ProductVariantConfig} from '../domain/product';
import {Inventory} from '@app/domain/inventory';
import {map, catchError, debounceTime, switchMap, share} from 'rxjs/operators';
import Fuse from 'fuse.js';
import {InventoryService} from '@app/service/inventory.service';
import {ProductsLocalStorage} from '@app/domain/products-local-storage';
import {CompanyService} from '@app/service/company.service';
import {PrintService} from '@app/service/print.service';
import {currencyList} from '@app/pipe/moneda.pipe';
import {Printer} from '@app/domain/printer';
import {ImagenProducto} from '@app/domain/imagen-producto';

export interface ProductSearchParams {
    filterGlobal?: string;
    codigo?: string;
    codigoBarra?: string;
    nombre?: string;
    mostrarEnRestaurante?: boolean;
    favorito?: boolean;
    idCategoria?: any[];
    idCanal?: any;
    idPuntoVenta?: any;
    idPuntoVentaOrder?: any;
    limit?: number;
    sortField?: string;
    sortOrder?: number;
    filtrosAdicionalesEnBusquedaProductos?: boolean;
}

export interface ProductSearchResult {
    products: Product[];
    fromLocalStorage: boolean;
}

export interface ProductImagesResponse {
    id?: number;
    idProducto?: number;
    productId?: number;
    imagenes?: ImagenProducto[];
    images?: ImagenProducto[];
}

export interface ProductCategoryAssignment {
    id: number;
    categoriaMenu?: {id: number} | null;
    categoriaCatalogo?: {id: number} | null;
}

export type ProductOrderUpdate =
    | {id: number; ordenMenu: number}
    | {id: number; ordenCatalogo: number};

export interface ProductFavoriteOrderUpdate {
    id: number;
    numeroOrdenFavorito: number;
}

@Injectable({
    providedIn: 'root'
})
export class ProductService {

    private static readonly INVENTORY_ENRICHMENT_DEBOUNCE_MS = 400;

    baseUrl: string = environment.endpointApi;
    graphqlUrl: string = environment.endpointApi.replace(/\/api$/, '') + '/graphql';

    private inventoryEnrichmentRequest$ = new Subject<{ products: Product[]; puntoVentaId: number }>();

    // Se emite recién cuando el usuario deja de escribir (debounce), evitando llamar a inventario en cada tecleada.
    readonly inventoryEnriched$: Observable<Product[]> = this.inventoryEnrichmentRequest$.pipe(
        debounceTime(ProductService.INVENTORY_ENRICHMENT_DEBOUNCE_MS),
        switchMap(({products, puntoVentaId}) => this.enrichProductsWithInventory(products, puntoVentaId)),
        share()
    );

    constructor(private http: HttpClient, private injector: Injector) {
    }

    findAll(params: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/products?${params}`);
    }

    findAllFlat(params: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/products/flat?${params}`);
    }

    findAllForSearch(params: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/products/search?${params}`);
    }

    findImagesByProductIds(productIds: number[]): Observable<ProductImagesResponse[]> {
        const ids = this.getUniqueProductIds(productIds);
        if (ids.length === 0) {
            return of([]);
        }
        return this.http.post<ProductImagesResponse[]>(
            `${this.baseUrl}/products/operations/images/by-product-ids`,
            ids,
            {headers: {hideLoader: 'S'}}
        );
    }

    findTopOrders(params): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/products/operations/top-orders?${params}`);
    }

    findAllPage(params: string): Observable<Page<Product>> {
        return this.findAll(params).pipe(map(data => data as Page<Product>));
    }

    findAllList(params: string): Observable<Product[]> {
        return this.findAll(params).pipe(map(data => data as Product[]));
    }

    findById(id: number): Observable<Product> {
        return this.http.get<Product>(`${this.baseUrl}/products/${id}`);
    }

    findProductsSearchGraphql(params: ProductSearchParams): Observable<Product[]> {
        const query = `
            query ProductsSearch(
                $filterGlobal: String,
                $codigo: String,
                $codigoBarra: String,
                $nombre: String,
                $mostrarEnRestaurante: Boolean,
                $favorito: Boolean,
                $idCategoria: [ID!],
                $idCanal: ID,
                $idPuntoVenta: ID,
                $idPuntoVentaOrder: ID,
                $limit: Int,
                $sortField: String,
                $sortOrder: Int
            ) {
                productsSearch(
                    filterGlobal: $filterGlobal,
                    codigo: $codigo,
                    codigoBarra: $codigoBarra,
                    nombre: $nombre,
                    mostrarEnRestaurante: $mostrarEnRestaurante,
                    favorito: $favorito,
                    idCategoria: $idCategoria,
                    idCanal: $idCanal,
                    idPuntoVenta: $idPuntoVenta,
                    idPuntoVentaOrder: $idPuntoVentaOrder,
                    limit: $limit,
                    sortField: $sortField,
                    sortOrder: $sortOrder
                ) {
                    id
                    nombre
                    codigo
                    codigoBarra
                    detalle
                    detalleMenu
                    moneda
                    tipoProducto
                    igv
                    recargoConsumo
                    precioVentaMinimo
                    preciosPorPuntoVenta
                    numeroVentas
                    precioRecurrenteVenta
                    puntosCanje
                    favorito
                    numeroOrdenFavorito
                    categoria {
                        id
                        codigo
                        nombre
                        imagenUrl
                    }
                    categoriaMenu {
                        id
                        codigo
                        nombre
                        imagenUrl
                    }
                    imagenes {
                        id
                        imagen
                        numeroOrden
                        porDefecto
                    }
                }
            }
        `;
        return this.http.post<any>(this.graphqlUrl, {query, variables: params}, {
            headers: {hideLoader: 'S', isPrinting: 'S'
        }}).pipe(
            map(response => response?.data?.productsSearch || [])
        );
    }

    findProductsMenuClientMenuGraphql(params: {
        mostrarEnRestaurante?: boolean;
        limit?: number;
    }): Observable<Product[]> {
        const query = `
            query ProductsMenuClientMenu(
                $mostrarEnRestaurante: Boolean,
                $limit: Int
            ) {
                productsSearch(
                    mostrarEnRestaurante: $mostrarEnRestaurante,
                    limit: $limit
                ) {
                    id
                    nombre
                    codigo
                    ordenMenu
                    ordenCatalogo
                    categoriaMenu {
                        id
                        nombre
                    }
                    categoria {
                        id
                        nombre
                    }
                    imagenes {
                        id
                        imagen
                        numeroOrden
                        porDefecto
                    }
                }
            }
        `;
        const variables = {
            mostrarEnRestaurante: params.mostrarEnRestaurante !== false,
            limit: params.limit || 1000
        };
        return this.http.post<any>(this.graphqlUrl, {query, variables}, {
            headers: {hideLoader: 'S', isPrinting: 'S'}
        }).pipe(
            map(response => response?.data?.productsSearch || [])
        );
    }

    findProductsCatalogConfiguration(limit: number = 1000): Observable<Product[]> {
        const safeLimit = Math.min(Math.max(limit || 1, 1), 1000);
        return this.findAllList(`paginacion=false&catalogo=true&limit=${safeLimit}`);
    }

    findProductGraphql(params: {id: any}): Observable<Product> {
        const query = `
            query Product($id: ID!) {
                product(id: $id) {
                    id
                    nombre
                    codigo
                    codigoBarra
                    detalle
                    detalleMenu
                    moneda
                    tipoProducto
                    igv
                    recargoConsumo
                    precioVentaMinimo
                    preciosPorPuntoVenta
                    numeroVentas
                    precioRecurrenteVenta
                    puntosCanje
                    categoria {
                        id
                        codigo
                        nombre
                    }
                    categoriaMenu {
                        id
                        codigo
                        nombre
                    }
                    imagenes {
                        id
                        imagen
                        numeroOrden
                        porDefecto
                    }
                    preciosVenta {
                        id
                        precio
                        precioNeto
                        margenUtilidad
                        unidadesMayoreo
                        tipoPrecio
                        nombre
                        puntoVenta { id nombre }
                        canalVenta { id codigo nombreComercial }
                    }
                    inventarios {
                        id
                        stock
                        stockAnterior
                        stockAlternativo
                        stockAlternativoAnterior
                        puntoVenta { id nombre }
                    }
                    grupos {
                        id
                        nombre
                        etiqueta
                        requerido
                        forzarMinimo
                        forzarMaximo
                        permitirCantidad
                        orden
                        tipo
                        opciones {
                            id
                            nombre
                            porcion
                            precio
                            porDefecto
                            orden
                            producto {
                                id
                                nombre
                                codigo
                                codigoBarra
                                moneda
                                tipoProducto
                                igv
                                recargoConsumo
                                precioVentaMinimo
                                categoria {
                                    id
                                    codigo
                                    nombre
                                    imagenUrl
                                }
                                imagenes {
                                    id
                                    imagen
                                    numeroOrden
                                    porDefecto
                                }
                            }
                        }
                    }
                    preparaciones {
                        id
                        requerido
                        preparacion {
                            id
                            nombre
                            seleccionMultiple
                            opciones {
                                id
                                opcion
                            }
                        }
                    }
                }
            }
        `;
        return this.http.post<any>(this.graphqlUrl, {query, variables: params}, {
            headers: {hideLoader: 'S', isPrinting: 'S'}
        }).pipe(
            map(response => response?.data?.product)
        );
    }

    findVariantsConfig(id: number, idPuntoVenta?: number): Observable<ProductVariantConfig> {
        const params = idPuntoVenta ? `?idPuntoVenta=${idPuntoVenta}` : '';
        return this.http.get<ProductVariantConfig>(`${this.baseUrl}/products/${id}/variants-config${params}`);
    }

    findVariantsLite(id: number): Observable<ProductVariant[]> {
        return this.http.get<ProductVariant[]>(`${this.baseUrl}/products/${id}/variants-lite`);
    }

    findAttributeDefinitions(): Observable<ProductAttributeDefinition[]> {
        return this.http.get<ProductAttributeDefinition[]>(`${this.baseUrl}/product-attribute-definitions`);
    }


    save(idEmpresa, model: Product): Observable<Product> {
        return this.http.post<Product>(`${this.baseUrl}/products`, model);
    }

    update(idEmpresa, model: Product): Observable<Product> {
        return this.http.put<Product>(`${this.baseUrl}/products`, model);
    }

    updateVariantsConfig(id: number, model: ProductVariantConfig, idPuntoVenta?: number): Observable<ProductVariantConfig> {
        const params = idPuntoVenta ? `?idPuntoVenta=${idPuntoVenta}` : '';
        return this.http.put<ProductVariantConfig>(`${this.baseUrl}/products/${id}/variants-config${params}`, model);
    }

    deleteById(idEmpresa, id: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/products/${id}`);
    }

    importFromExcel(formData): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/products/import`, formData);
    }

    startInventory(model: any): Observable<Inventory> {
        return this.http.post<Inventory>(`${this.baseUrl}/products/operations/startInventory`, model);
    }

    deleteByIdEmpresa(idEmpresa): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/root/products/${idEmpresa}`);
    }

    processPurchaseUnitPriceByIdEmpresa(idEmpresa): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/root/ticket-detail/process-product-price/${idEmpresa}`, null);
    }

    platillosUpdatePrices(): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/products/platillos/update-precios`);
    }

    updateProductsMenu(models: ProductCategoryAssignment[]): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/products/menu/update`, models);
    }

    updateProductsCatalog(models: ProductCategoryAssignment[]): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/products/catalog/update`, models);
    }

    updateProductMenuOrder(orders: Array<{id: number; ordenMenu: number}>): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/products/menu-order`, orders);
    }

    updateProductCatalogOrder(orders: Array<{id: number; ordenCatalogo: number}>): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/products/catalog-order`, orders);
    }

    updateProductFavoriteOrder(orders: ProductFavoriteOrderUpdate[]): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/products/favorite-order`, orders);
    }

    getProductsFromLocalStorage(contextoVentas: boolean = false): Product[] {
        const productsJson = localStorage.getItem('products_flat');
        if (productsJson) {
            try {
                const data = JSON.parse(productsJson) as ProductsLocalStorage;
                const products = data.products || [];
                return contextoVentas ? products.filter(product => !product.ocultarEnBuscadorVentas) : products;
            } catch (e) {
                console.error('Error parsing products from localStorage', e);
                return null;
            }
        }
        return null;
    }

    findMatchingProductIdsLocally(query: string,
                                  incluirFiltrosAdicionales: boolean = true): number[] | null {
        const products = this.getProductsFromLocalStorage();
        if (!Array.isArray(products)) {
            return null;
        }

        const normalizedQuery = this.normalizeSearchText(query);
        if (!normalizedQuery) {
            return null;
        }

        const {substringMatches, marcaCategoriaMatches, fuzzyOnlyMatches} =
            this.searchProductsWithFuse(products, normalizedQuery, incluirFiltrosAdicionales);

        return this.getUniqueProductIds([
            ...substringMatches,
            ...marcaCategoriaMatches,
            ...fuzzyOnlyMatches
        ].map(product => product.id));
    }

    // Verifica si el listado local tiene los campos necesarios para resolver los filtros pedidos.
    canFindProductsSearchLocally(products: Product[], params: ProductSearchParams): boolean {
        if (!products || products.length === 0) {
            return false;
        }

        if (params.codigo && !this.fieldExistsInProducts(products, 'codigo')) {
            return false;
        }
        if (params.codigoBarra && !this.fieldExistsInProducts(products, 'codigoBarra')) {
            return false;
        }
        if (params.nombre && !this.fieldExistsInProducts(products, 'nombre')) {
            return false;
        }
        if (params.mostrarEnRestaurante !== undefined && !this.fieldExistsInProducts(products, 'mostrarEnRestaurante')) {
            return false;
        }
        if (params.favorito !== undefined && !this.fieldExistsInProducts(products, 'favorito')) {
            return false;
        }
        if (params.favorito === true && !this.fieldExistsInProducts(products, 'numeroVentas')) {
            return false;
        }
        if (params.favorito === true && !this.fieldExistsInProducts(products, 'numeroOrdenFavorito')) {
            return false;
        }
        if (params.idPuntoVenta && !this.fieldExistsInProducts(products, 'puntosDeVenta')) {
            return false;
        }
        if (params.sortField && !this.fieldExistsInProducts(products, params.sortField)) {
            return false;
        }

        if (params.idCategoria && params.idCategoria.length > 0) {
            return this.fieldExistsInProducts(products, 'categoria.id') ||
                this.fieldExistsInProducts(products, 'categoriaMenu.id');
        }

        return true;
    }

    // Aplica filtros de productosSearch sobre products_flat y reutiliza la busqueda local general.
    findProductsSearchLocally(products: Product[], params: ProductSearchParams): Observable<Product[]> {
        if (params.favorito === true) {
            return forkJoin([
                this.findProductsSearchLocallyInternal(products, params),
                this.findProductsSearchLocallyInternal(products, this.buildTopSellingSearchParams(params))
            ]).pipe(
                map(([favorites, topSelling]) => this.mergeUniqueProducts(
                    this.sortFavoriteProducts(favorites), topSelling
                ))
            );
        }

        return this.findProductsSearchLocallyInternal(products, params);
    }

    private findProductsSearchLocallyInternal(products: Product[], params: ProductSearchParams): Observable<Product[]> {
        const limit = params.limit || 50;
        const puntoVentaId = Number(params.idPuntoVentaOrder || params.idPuntoVenta);
        const sortParams = this.buildSortParams(params.sortField, params.sortOrder);
        let filtered = [...products];
        if (params.mostrarEnRestaurante !== undefined) {
            filtered = filtered.filter(p => Boolean(p.mostrarEnRestaurante) === params.mostrarEnRestaurante);
        }

        if (params.favorito !== undefined) {
            filtered = filtered.filter(p => Boolean(p.favorito) === params.favorito);
        }

        if (params.idCategoria && params.idCategoria.length > 0) {
            const categoryIds = params.idCategoria.map(id => String(id));
            filtered = filtered.filter(p => {
                const categoriaId = p.categoria?.id != null ? String(p.categoria.id) : null;
                const categoriaMenuId = p.categoriaMenu?.id != null ? String(p.categoriaMenu.id) : null;
                return (categoriaId !== null && categoryIds.includes(categoriaId)) ||
                    (categoriaMenuId !== null && categoryIds.includes(categoriaMenuId));
            });
        }

        if (params.codigo) {
            filtered = this.filterExactTextField(filtered, 'codigo', params.codigo);
        }

        if (params.codigoBarra) {
            filtered = this.filterExactTextField(filtered, 'codigoBarra', params.codigoBarra);
        }

        return this.filterProductsLocally(
            filtered,
            params.nombre || params.filterGlobal || '',
            sortParams,
            limit,
            puntoVentaId,
            !!params.idPuntoVenta,
            params.filtrosAdicionalesEnBusquedaProductos
        );
    }

    private buildTopSellingSearchParams(params: ProductSearchParams): ProductSearchParams {
        return {
            mostrarEnRestaurante: true,
            favorito: false,
            idPuntoVenta: params.idPuntoVenta,
            idPuntoVentaOrder: params.idPuntoVentaOrder,
            limit: 10,
            sortField: 'numeroVentas',
            sortOrder: -1,
            filtrosAdicionalesEnBusquedaProductos: params.filtrosAdicionalesEnBusquedaProductos
        };
    }

    private mergeUniqueProducts(primary: Product[], additional: Product[]): Product[] {
        const productIds = new Set<any>();
        return [...(primary || []), ...(additional || [])].filter(product => {
            if (product?.id == null) {
                return true;
            }
            const productId = String(product.id);
            if (productIds.has(productId)) {
                return false;
            }
            productIds.add(productId);
            return true;
        });
    }

    private sortFavoriteProducts(products: Product[]): Product[] {
        return [...(products || [])].sort((first, second) => {
            const firstOrder = first.numeroOrdenFavorito == null ? Number.MAX_SAFE_INTEGER : first.numeroOrdenFavorito;
            const secondOrder = second.numeroOrdenFavorito == null ? Number.MAX_SAFE_INTEGER : second.numeroOrdenFavorito;
            return firstOrder - secondOrder ||
                (first.nombre || '').localeCompare(second.nombre || '') ||
                Number(first.id) - Number(second.id);
        });
    }

    enrichProductsWithImages(products: Product[]): Observable<Product[]> {
        const sourceProducts = products || [];
        const productIdsWithoutImages = this.getUniqueProductIds(
            sourceProducts
                .filter(product => !product.imagenes || product.imagenes.length === 0)
                .map(product => product.id)
        );

        if (productIdsWithoutImages.length === 0) {
            return of(sourceProducts);
        }

        return this.findImagesByProductIds(productIdsWithoutImages).pipe(
            map(imagesByProduct => {
                const imagesMap = new Map<number, ImagenProducto[]>();
                (imagesByProduct || []).forEach(item => {
                    const productId = this.getProductImagesResponseId(item);
                    const images = this.getProductImagesResponseImages(item);
                    if (productId && images.length > 0) {
                        imagesMap.set(productId, images);
                    }
                });

                return sourceProducts.map(product => {
                    const productId = Number(product.id);
                    return imagesMap.has(productId) ? {...product, imagenes: imagesMap.get(productId)} : product;
                });
            }),
            catchError(error => {
                console.error('Error obteniendo imagenes de productos:', error);
                return of(sourceProducts);
            })
        );
    }

    mergeProductImages(products: Product[], enrichedProducts: Product[]): Product[] {
        if (!products || !products.length || !enrichedProducts || !enrichedProducts.length) {
            return products || [];
        }

        const enrichedById = new Map(
            enrichedProducts
                .filter(product => product.id && product.imagenes && product.imagenes.length > 0)
                .map(product => [Number(product.id), product.imagenes])
        );

        if (enrichedById.size === 0) {
            return products;
        }

        return products.map(product => {
            const productId = Number(product.id);
            return enrichedById.has(productId) ? {...product, imagenes: enrichedById.get(productId)} : product;
        });
    }

    // Busca productos en memoria, filtrando por punto de venta, texto, orden y limite.
    filterProductsLocally(
        products: Product[],
        query: string,
        sortParams: string[] = [],
        limit: number = 50,
        puntoVentaId?: number,
        buscarPorPuntoVenta: boolean = false,
        incluirFiltrosAdicionales: boolean = true
    ): Observable<Product[]> {
        // Filtrar por punto de venta si es necesario
        let filtered = products;
        if (buscarPorPuntoVenta && puntoVentaId) {
            filtered = products.filter(p => {
                const puntosDeVenta = p.puntosDeVenta || [];
                return puntosDeVenta.length !== 0 && puntosDeVenta?.some(pv => pv?.id === puntoVentaId);
            });
        }

        const sortInfo = this.parseSortParams(sortParams);
        let results: Product[];

        if (query && query.trim().length > 0) {
            // El orden secundario se aplica dentro de cada nivel de relevancia (coincidencia
            // exacta en nombre/codigo, luego marca/categoria, luego difusa) en vez de sobre el
            // resultado combinado, para que un orden como "Más recientes" no termine mostrando
            // arriba un producto que no coincide realmente con el texto buscado solo por ser
            // más nuevo que los que sí coinciden.
            const {exactCodeMatches, substringMatches, marcaCategoriaMatches, fuzzyOnlyMatches} =
                this.searchProductsWithFuse(filtered, query, incluirFiltrosAdicionales, limit);
            if (sortInfo.field && sortInfo.order !== null) {
                results = [
                    ...this.sortProductsLocally(exactCodeMatches, sortInfo.field, sortInfo.order),
                    ...this.sortProductsLocally(substringMatches, sortInfo.field, sortInfo.order),
                    ...this.sortProductsLocally(marcaCategoriaMatches, sortInfo.field, sortInfo.order),
                    ...this.sortProductsLocally(fuzzyOnlyMatches, sortInfo.field, sortInfo.order)
                ];
            } else {
                results = [...exactCodeMatches, ...substringMatches, ...marcaCategoriaMatches, ...fuzzyOnlyMatches];
            }
        } else if (sortInfo.field && sortInfo.order !== null) {
            results = this.sortProductsLocally(filtered, sortInfo.field, sortInfo.order);
        } else {
            results = filtered;
        }

        const limitedProducts = results.slice(0, limit);
        this.requestInventoryEnrichment(limitedProducts, puntoVentaId);
        return of(limitedProducts);
    }

    // Encola la solicitud de inventario; solo se ejecuta la última recibida tras el debounce (ver inventoryEnriched$).
    requestInventoryEnrichment(products: Product[], puntoVentaId?: number): void {
        if (!puntoVentaId || products.length === 0) {
            return;
        }
        this.inventoryEnrichmentRequest$.next({products, puntoVentaId});
    }

    private enrichProductsWithInventory(products: Product[], puntoVentaId?: number): Observable<Product[]> {
        // Si no hay puntoVentaId o no hay productos, devolver directamente
        if (!puntoVentaId || products.length === 0) {
            return of(products);
        }

        // Extraer IDs de productos para consultar inventario
        const productIds = products.map(p => p.id).filter(id => id);

        if (productIds.length === 0) {
            return of(products);
        }

        // Obtener InventoryService de forma lazy para evitar dependencia circular
        const inventoryService = this.injector.get(InventoryService);

        // Llamar al endpoint de inventario
        return inventoryService.findByProductIds(productIds, puntoVentaId).pipe(
            map((inventories: any[]) => {
                // Asignar inventario a cada producto
                const inventoryMap = new Map();
                inventories.forEach(inv => {
                    if (inv.id) {
                        inventoryMap.set(inv.id, inv.inventario);
                    }
                });

                return products.map(product => {
                    if (inventoryMap.has(product.id)) {
                        return {...product, inventarios: [inventoryMap.get(product.id)]};
                    }
                    return product;
                });
            }),
            catchError(error => {
                // En caso de error, devolver productos sin inventario
                console.error('Error obteniendo inventarios:', error);
                return of(products);
            })
        );
    }


    private parseSortParams(sortParams: string[]): {field: string, order: number} {
        if (!sortParams || sortParams.length === 0) {
            return {field: null, order: null};
        }

        let sortField = null;
        let sortOrder = null;

        sortParams.forEach(param => {
            if (param.startsWith('sortField=')) {
                sortField = param.split('=')[1];
            }
            if (param.startsWith('sortOrder=')) {
                sortOrder = parseInt(param.split('=')[1], 10);
            }
        });

        return {field: sortField, order: sortOrder};
    }

    // Convierte el sort de GraphQL al formato usado por la busqueda local.
    private buildSortParams(sortField?: string, sortOrder?: number): string[] {
        if (!sortField || sortOrder === null || sortOrder === undefined) {
            return [];
        }
        return [
            `sortOrder=${sortOrder}`,
            `sortField=${sortField}`
        ];
    }

    // Comprueba si al menos un producto local contiene el campo indicado.
    private fieldExistsInProducts(products: any[], field: string): boolean {
        if (!field || !products || products.length === 0) {
            return false;
        }
        return products.some(prod => this.getFieldValue(prod, field) !== undefined);
    }

    // Obtiene un valor simple o anidado usando rutas tipo categoria.id.
    private getFieldValue(product: any, field: string): any {
        const keys = field.split('.');
        let cur: any = product;
        for (const k of keys) {
            if (cur == null) {
                return undefined;
            }
            const hasProperty = Object.prototype.hasOwnProperty.call(cur, k) || (k in cur);
            if (!hasProperty) {
                return undefined;
            }
            cur = cur[k];
        }
        return cur;
    }

    // Filtra por coincidencia exacta para campos como codigo o codigoBarra.
    private filterExactTextField(products: Product[], field: string, value: string): Product[] {
        const normalizedValue = this.normalizeSearchText(value);
        if (!normalizedValue) {
            return products;
        }
        return products.filter(product => {
            const fieldValue = this.getFieldValue(product, field);
            const normalizedFieldValue = this.normalizeSearchText(fieldValue);
            return normalizedFieldValue === normalizedValue;
        });
    }

    // Ejecuta la busqueda flexible por nombre, codigo, codigo de barra, marca y categoria.
    // Prioriza el código completo y luego separa coincidencias en tres niveles de relevancia:
    // 1. Todos los términos en nombre/codigo/codigoBarra, en cualquier posición y orden.
    // 2. Todos los términos incluyendo marca/categoria, cuando están habilitados.
    // 3. Coincidencias difusas de Fuse, solo para tolerar errores de tipeo.
    // Deben mantenerse separados incluso al aplicar un orden secundario (ver filterProductsLocally),
    // ya que Fuse por si solo puede rankear productos irrelevantes por encima de coincidencias
    // reales en textos cortos (ej. "lija" matcheando "ALICATE").
    private searchProductsWithFuse(products: Product[], query: string, incluirFiltrosAdicionales: boolean = true, limit: number = 50):
        {exactCodeMatches: Product[], substringMatches: Product[], marcaCategoriaMatches: Product[], fuzzyOnlyMatches: Product[]} {
        const tokens = this.tokenizeProductSearchText(query);
        const meaningfulTerms = tokens.filter(term => !/^(de|del|la|el|los|las|para)$/.test(term));
        const terms = Array.from(new Set(meaningfulTerms.length ? meaningfulTerms : tokens));
        const exactCodeMatches: Product[] = [];
        const substringMatches: Product[] = [];
        const marcaCategoriaMatches: Product[] = [];
        const fuzzyCandidates: {product: Product, words: string[]}[] = [];
        if (terms.length === 0) {
            return {exactCodeMatches, substringMatches, marcaCategoriaMatches, fuzzyOnlyMatches: []};
        }

        // Nunca corregir números/códigos ni términos muy cortos: 3/4 no debe encontrar 13/4 o 3/8.
        const fuzzyTerms = terms.filter(term => /^[a-z]{4,}$/.test(term));
        const strictTerms = terms.filter(term => !fuzzyTerms.includes(term));
        const matches = (words: string[], required: string[]) => required.every(term =>
            words.some(word => this.matchesProductSearchTerm(word, term))
        );

        const compactCode = (value: any) => this.normalizeSearchText(value).replace(/[\s_-]+/g, '');
        const queryCode = compactCode(query);
        // Los códigos numéricos del catálogo pueden llevar relleno: 4959 encuentra 00000000004959.
        // La equivalencia se limita al código de producto; no altera medidas ni códigos de barras.
        const numericCode = (value: string) => /^\d+$/.test(value) ? value.replace(/^0+(?=\d)/, '') : '';
        products.forEach(product => {
            const productNumericCode = numericCode(compactCode(product.codigo));
            if (queryCode && ([product.codigo, product.codigoBarra].some(value => compactCode(value) === queryCode) ||
                (productNumericCode && productNumericCode === numericCode(queryCode)))) {
                exactCodeMatches.push(product);
                return;
            }
            const primaryWords = [
                ...this.tokenizeProductSearchText(product.nombre),
                ...this.tokenizeProductSearchText(product.codigo),
                ...this.tokenizeProductSearchText(product.codigoBarra),
                ...(productNumericCode ? [productNumericCode] : [])
            ];
            if (matches(primaryWords, terms)) {
                substringMatches.push(product);
                return;
            }
            const words = incluirFiltrosAdicionales ? [
                ...primaryWords,
                ...this.tokenizeProductSearchText(product.nombreMarca),
                ...this.tokenizeProductSearchText(product.categoria?.nombre)
            ] : primaryWords;
            if (incluirFiltrosAdicionales && matches(words, terms)) {
                marcaCategoriaMatches.push(product);
            } else if (fuzzyTerms.length > 0 && matches(words, strictTerms)) {
                fuzzyCandidates.push({product, words});
            }
        });

        // Si las coincidencias exactas ya llenan el límite, las difusas no llegarían a mostrarse.
        const skipFuzzy = exactCodeMatches.length + substringMatches.length + marcaCategoriaMatches.length >= limit;
        const fuzzyOnlyMatches = skipFuzzy ? [] : this.findFuzzyProductMatches(fuzzyCandidates, fuzzyTerms);

        return {exactCodeMatches, substringMatches, marcaCategoriaMatches, fuzzyOnlyMatches};
    }

    private findFuzzyProductMatches(candidates: {product: Product, words: string[]}[], terms: string[]): Product[] {
        if (!candidates.length || !terms.length) {
            return [];
        }
        // Indexar palabras únicas evita repetir todo el catálogo por cada término.
        const vocabulary = new Set<string>();
        const entries = candidates.map(candidate => {
            const words: string[] = candidate.words.join(' ').match(/[a-z]{4,}/g) || [];
            words.forEach(word => vocabulary.add(word));
            return {product: candidate.product, words};
        });
        const fuse = new Fuse(Array.from(vocabulary), {
            threshold: 0.3,
            distance: 0,
            ignoreLocation: false,
            includeScore: true,
            useExtendedSearch: false
        });
        // El error debe estar en la palabra, no en un fragmento interior: broca ≠ fabricación.
        const termScores = terms.map(term => {
            const scores = new Map(fuse.search(term).map(result => [result.item, result.score || 0]));
            vocabulary.forEach(word => {
                if (word.includes(term)) {
                    scores.set(word, 0);
                }
            });
            return scores;
        });
        return entries.map(entry => ({
            product: entry.product,
            score: termScores.reduce((sum, scores) =>
                sum + entry.words.reduce((best, word) => Math.min(best, scores.get(word) ?? Infinity), Infinity), 0)
        })).filter(entry => Number.isFinite(entry.score))
            .sort((a, b) => a.score - b.score)
            .map(entry => entry.product);
    }

    private tokenizeProductSearchText(value: any): string[] {
        // NFKD convierte ¾ a 3⁄4. Normalizar ambos lados permite espacios y barras Unicode.
        const normalized = this.normalizeSearchText(value)
            .replace(/(\d)([¼½¾⅐-⅞])/g, '$1 $2').normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[⁄∕]/g, '/')
            .replace(/(\d)\s*\//g, '$1/')
            .replace(/\/\s+(?=\d)/g, '/')
            .replace(/(\d),(?=\d)/g, '$1.')
            .replace(/(\d)["″”]/g, '$1pulg')
            .replace(/(\d(?:mm2?|cm|mts?|m|pulg)?)[ø\s]*[x×*]\s*(?=\d)/g, '$1 x ')
            .replace(/\b(mts?|metros?)\b/g, 'm')
            .replace(/(\d)\s*(mm2?|cm|mts?|m|metros?|pulgadas?|pulg|uni|und|unidades|u|kv|kw|ah|v|w|a|l)\b/g,
                (_, number, unit) => number + (/^(mts?|metros?)$/.test(unit) ? 'm' :
                    /^(uni|und|unidades|u)$/.test(unit) ? 'u' : /^pulg/.test(unit) ? 'pulg' : unit))
            // Una etiqueta pegada (P1 1/2) conserva P1 y permite reconocer la medida mixta.
            // Excluir unidades: MM2 450/750V no es una fracción mixta.
            .replace(/\b([a-z])(\d+)(?=\s+\d+\/)/g, (word, prefix, whole) =>
                /^(m|v|w|a|l)$/.test(prefix) ? word : word + ' ' + whole)
            .replace(/(^|[^a-z0-9./])(\d+)[\s-]+(\d+)\/(\d*)/g, '$1$2~$3/$4');
        const words: string[] = normalized.match(/[a-z0-9]+(?:[./_~-][a-z0-9]+)*\/?/g) || [];
        // Modelos/códigos como PG-13.5 o FOB023 también admiten «PG 13.5» y «FOB 023».
        const result = words.reduce((terms, word) => {
            terms.push(word);
            const codeNumber = /^(?:mm|cm|m)[23]$/.test(word) ? null : word.match(/^[a-z]+[-_]?(\d+(?:\.\d+)?)/);
            if (codeNumber) {
                terms.push(codeNumber[1]);
            }
            return terms;
        }, [] as string[]);
        // Conservar la secuencia además de sus componentes: 4 x 4 x 2 no equivale a 4 x 2.
        for (let index = 0; index < words.length; index++) {
            if (words[index + 1] !== 'x' && !words[index].endsWith('x')) {
                continue;
            }
            const dimensions: string[] = [];
            let end = index;
            let incomplete = false;
            while (end < words.length) {
                const trailingX = words[end].endsWith('x');
                const part = trailingX ? words[end].slice(0, -1) : words[end];
                if (this.matchesProductSearchMeasure(part, part) !== true) {
                    break;
                }
                dimensions.push(part);
                if (trailingX || (words[end + 1] === 'x' && end + 2 === words.length)) {
                    incomplete = true;
                    break;
                }
                if (words[end + 1] !== 'x') {
                    break;
                }
                end += 2;
            }
            if (dimensions.length > 1 || incomplete) {
                result.push('@' + dimensions.join('x') + (incomplete ? 'x' : ''));
                index = end;
            }
        }
        return result;
    }

    private matchesProductSearchTerm(word: string, term: string): boolean {
        if (term.startsWith('@')) {
            const incomplete = term.endsWith('x');
            const requested = term.slice(1, incomplete ? -1 : undefined).split('x');
            const candidate = word.startsWith('@') ? word.slice(1).split('x') : [];
            const matchingLength = incomplete ? candidate.length > requested.length : candidate.length === requested.length;
            return matchingLength && requested.every((part, index) =>
                this.matchesProductSearchMeasure(candidate[index], part) === true
            );
        }
        // Antes del separador, admitir 1, 1/2 y 1.5, sin confundir 1 con 11 o con una caja de 100u.
        if (/^\d+$/.test(term)) {
            return word.match(/^\d+/)?.[0] === term;
        }
        // Admitir también mixtas en curso: 4 1/ encuentra 4 1/2, sin confundirlo con 1/2 o 14 1/2.
        if (/^\d+(?:~\d+)?\/$/.test(term)) {
            return word.startsWith(term) && /^\d+(?:~\d+)?\/\d+/.test(word);
        }
        const measureMatch = this.matchesProductSearchMeasure(word, term);
        if (measureMatch !== null) {
            return measureMatch;
        }
        // Los identificadores admiten guiones opcionales, sin corrección difusa de sus números.
        return word.replace(/[-_]/g, '').includes(term.replace(/[-_]/g, ''));
    }

    private matchesProductSearchMeasure(word: string, term: string): boolean | null {
        const measure = /^(\d+(?:~\d+\/\d+|[./]\d+)*)(mm2?|cm|m|pulg|u|kv|kw|ah|v|w|a|l)?$/;
        const requested = term?.match(measure);
        if (!requested) {
            return null;
        }
        const candidate = word?.match(measure);
        if (!candidate || (requested[2] && candidate[2] && requested[2] !== candidate[2])) {
            return false;
        }
        return requested[1] === candidate[1] ||
            (Number.isFinite(Number(requested[1])) && Number(requested[1]) === Number(candidate[1]));
    }

    // Normaliza valores antes de compararlos en busquedas locales.
    private normalizeSearchText(value: any): string {
        return value === null || value === undefined ? '' : String(value).trim().toLowerCase();
    }

    private getUniqueProductIds(productIds: any[]): number[] {
        return Array.from(new Set(
            (productIds || [])
                .map(id => Number(id))
                .filter(id => Number.isFinite(id) && id > 0)
        ));
    }

    private getProductImagesResponseId(item: ProductImagesResponse): number {
        return Number(item?.idProducto || item?.productId || item?.id);
    }

    private getProductImagesResponseImages(item: ProductImagesResponse): ImagenProducto[] {
        if (!item) {
            return [];
        }
        if (Array.isArray(item.imagenes)) {
            return item.imagenes;
        }
        if (Array.isArray(item.images)) {
            return item.images;
        }
        return [];
    }

    private sortProductsLocally(products: Product[], field: string, order: number): Product[] {
        return [...products].sort((a, b) => {
            let valueA = this.getFieldValue(a, field);
            let valueB = this.getFieldValue(b, field);

            if (valueA === null || valueA === undefined) {
                valueA = 0;
            }
            if (valueB === null || valueB === undefined) {
                valueB = 0;
            }

            if (typeof valueA === 'string' && typeof valueB === 'string') {
                valueA = valueA.toLowerCase();
                valueB = valueB.toLowerCase();
            }

            if (valueA < valueB) {
                return order === 1 ? -1 : 1;
            }
            if (valueA > valueB) {
                return order === 1 ? 1 : -1;
            }
            return 0;
        });
    }

    buildProductBarcodeLines(product: Product, printer: Printer): any[] {
        const precioDefault = (product.preciosVenta || []).find(p => p.tipoPrecio === 'POR_DEFECTO');
        const moneda = currencyList.find(c => c.codigoMoneda === product.moneda);
        const simbolo = moneda ? moneda.simbolo : 'Desc.';
        const precio = precioDefault ? `${simbolo} ${precioDefault.precio}` : '';
        return [
            { text: product.nombre, fontSize: Number(printer.fontSizeCodigoBarraProducto) || 6, italic: false, horizontalAlignment: 'center' },
            { text: product.codigoBarra, fontSize: Number(printer.fontSizeCodigoBarra) || 6, italic: false, esCodigoBarra: true, horizontalAlignment: 'center', heightCodigoBarra: Number(printer.heightCodigoBarra) || 5 },
            { text: product.codigoBarra, fontSize: Number(printer.fontSizeCodigoBarraNombre) || 7, italic: false, horizontalAlignment: 'center' },
            { text: precio, fontSize: Number(printer.fontSizeCodigoBarraProductoPrecio) || 6, italic: false, horizontalAlignment: 'center' }
        ];
    }

    printBarcodeBatch(products: Product[], printer: Printer, officeCode: string, idCompany: number): void {
        const widthPage = Number(printer.anchoPapelSticker) || 50;
        const heightPage = Number(printer.altoPapelSticker) || 43;
        const verticalMargin = Number(printer.margenVertical) || 3;
        const horizontalMargin = Number(printer.margenHorizontal) || 5;
        const pages = products.map(product => ({
            lines: this.buildProductBarcodeLines(product, printer),
            widthPage,
            heightPage,
            verticalMargin,
            horizontalMargin
        }));
        const printService = this.injector.get(PrintService);
        printService.printCoffe({ printerName: printer.nombre, pages, event: 'printBatch', idCompany, officeCode });
    }

    printBarcodeCopias(product: Product, copies: number, printer: Printer, officeCode: string, idCompany: number): void {
        const widthPage = Number(printer.anchoPapelSticker) || 50;
        const heightPage = Number(printer.altoPapelSticker) || 43;
        const verticalMargin = Number(printer.margenVertical) || 3;
        const horizontalMargin = Number(printer.margenHorizontal) || 5;
        const lines = this.buildProductBarcodeLines(product, printer);
        const pages = Array.from({ length: copies }, () => ({ lines, widthPage, heightPage, verticalMargin, horizontalMargin }));
        const printService = this.injector.get(PrintService);
        printService.printCoffe({ printerName: printer.nombre, pages, event: 'printBatch', idCompany, officeCode });
    }

    private extractFlatProductFields(product: Product): any {
        return {
            id: product.id,
            nombre: product.nombre,
            moneda: product.moneda,
            preciosPorPuntoVenta: product.preciosPorPuntoVenta,
            preciosVenta: product.preciosVenta,
            createdOn: product.createdOn,
            numeroVentas: product.numeroVentas,
            precioRecurrenteVenta: product.precioRecurrenteVenta,
            favorito: product.favorito,
            numeroOrdenFavorito: product.numeroOrdenFavorito,
            puntosDeVenta: product.puntosDeVenta,
        };
    }

    updateFavoriteOrdersInLocalStorage(orders: ProductFavoriteOrderUpdate[]): void {
        const dataJson = localStorage.getItem('products_flat');
        if (!dataJson) {
            return;
        }
        try {
            const data = JSON.parse(dataJson) as ProductsLocalStorage;
            const orderByProductId = new Map(orders.map(order => [Number(order.id), order.numeroOrdenFavorito]));
            data.products.forEach(product => {
                const order = orderByProductId.get(Number(product.id));
                if (order != null) {
                    product.numeroOrdenFavorito = order;
                }
            });
            const timestamp = new Date().toISOString();
            data.lastUpdate = timestamp;
            localStorage.setItem('products_flat', JSON.stringify(data));
            this.updateTimestampInBackend(timestamp);
        } catch (error) {
            console.error('Error actualizando el orden de favoritos en localStorage', error);
        }
    }


    updateProductInLocalStorage(product: Product): void {
        const dataJson = localStorage.getItem('products_flat');
        if (!dataJson) {
            console.warn('No hay datos en localStorage, no se puede actualizar');
            return;
        }

        try {
            const data = JSON.parse(dataJson) as ProductsLocalStorage;

            const productFlat = this.extractFlatProductFields(product);
            const index = data.products.findIndex(p => p.id === product.id);

            if (index >= 0) {
                // Actualizar producto existente
                data.products[index] = productFlat;
            } else {
                // Agregar nuevo producto
                data.products.push(productFlat);
            }

            // Actualizar timestamp
            const newTimestamp = new Date().toISOString();
            data.lastUpdate = newTimestamp;

            // Guardar de vuelta en localStorage
            localStorage.setItem('products_flat', JSON.stringify(data));
            console.log(`Producto ${product.id} actualizado en localStorage`);

            // Actualizar timestamp en el backend
            this.updateTimestampInBackend(newTimestamp);
        } catch (e) {
            console.error('Error al actualizar producto en localStorage', e);
        }
    }

    removeProductFromLocalStorage(productId: number): void {
        const dataJson = localStorage.getItem('products_flat');
        if (!dataJson) {
            console.warn('No hay datos en localStorage');
            return;
        }

        try {
            const data = JSON.parse(dataJson) as ProductsLocalStorage;

            // Filtrar el producto a eliminar
            data.products = data.products.filter(p => p.id !== productId);

            // Actualizar timestamp
            const newTimestamp = new Date().toISOString();
            data.lastUpdate = newTimestamp;

            // Guardar de vuelta en localStorage
            localStorage.setItem('products_flat', JSON.stringify(data));
            console.log(`Producto ${productId} eliminado de localStorage`);

            // Actualizar timestamp en el backend
            this.updateTimestampInBackend(newTimestamp);
        } catch (e) {
            console.error('Error al eliminar producto de localStorage', e);
        }
    }


    syncProductsToLocalStorage(
        returnMessage: boolean = false,
        existingTimestamp?: string,
        shouldUpdateBackendIfNoTimestamp: boolean = false
    ): Observable<any> {
        const params = [`paginacion=${false}`];
        return this.findAllFlat(params.join('&')).pipe(
            map(productsFlat => {
                try {
                    let timestamp: string;
                    let shouldUpdateBackend = false;

                    // Si viene un timestamp existente, usarlo sin actualizar el backend
                    if (existingTimestamp) {
                        timestamp = existingTimestamp;
                        console.log('Usando timestamp existente de la Company:', timestamp);
                    } else {
                        // Si no hay timestamp proporcionado, intentar reutilizar el del localStorage
                        const localData = this.getLocalStorageData();
                        if (localData && localData.lastUpdate && !shouldUpdateBackendIfNoTimestamp) {
                            // Reutilizar timestamp existente en localStorage (sincronización manual)
                            timestamp = localData.lastUpdate;
                            console.log('Reutilizando timestamp del localStorage:', timestamp);
                        } else {
                            // Generar uno nuevo solo si no hay timestamp en localStorage o se debe actualizar backend
                            timestamp = new Date().toISOString();
                            shouldUpdateBackend = shouldUpdateBackendIfNoTimestamp;
                            console.log('Generando nuevo timestamp:', timestamp);
                        }
                    }

                    const data: ProductsLocalStorage = {
                        products: productsFlat,
                        lastUpdate: timestamp
                    };

                    localStorage.setItem('products_flat', JSON.stringify(data));
                    console.log(`${productsFlat.length} productos sincronizados en localStorage`);

                    // Solo actualizar el backend si se indica explícitamente
                    if (shouldUpdateBackend) {
                        this.updateTimestampInBackend(timestamp);
                        console.log('Actualizando timestamp en backend (primera sincronización)');
                    }

                    if (returnMessage) {
                        return {
                            success: true,
                            count: productsFlat.length,
                            message: `${productsFlat.length} productos sincronizados correctamente`
                        };
                    }
                    return productsFlat;
                } catch (e) {
                    console.error('Error al sincronizar productos en localStorage', e);
                    if (returnMessage) {
                        return {
                            success: false,
                            error: e,
                            message: 'Error al sincronizar productos'
                        };
                    }
                    throw e;
                }
            })
        );
    }

    private getLocalStorageData(): ProductsLocalStorage | null {
        const dataJson = localStorage.getItem('products_flat');
        if (!dataJson) {
            return null;
        }

        try {
            return JSON.parse(dataJson) as ProductsLocalStorage;
        } catch (e) {
            console.error('Error parseando localStorage:', e);
            return null;
        }
    }

    private updateTimestampInBackend(timestamp: string): void {
        // Usar Injector para obtener CompanyService de forma lazy y evitar dependencias circulares
        const companyService = this.injector.get(CompanyService);

        companyService.updateLocalTimestamp(timestamp).subscribe({
            next: () => {
                console.log('Timestamp actualizado en el backend:', timestamp);
            },
            error: (error: any) => {
                console.error('Error al actualizar timestamp en el backend:', error);
            }
        });
    }

}
